/**
 * Markdown for answers and plans (Phase 10): headings, paragraphs, lists
 * (nested by indentation), fenced code with its language, tables, quotes,
 * rules, and inline bold, italic, strikethrough, code and links. The parser
 * returns a tree the renderer turns into React elements, never HTML, so a
 * model's output cannot inject markup; only http(s) and mailto links are
 * links.
 */
export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong' | 'em' | 'del'; children: Inline[] }
  | { kind: 'link'; href: string; children: Inline[] };

export interface ListItem {
  children: Inline[];
  /** a nested list under the item */
  sub: Block | null;
  /** GitHub task list: [ ] or [x] */
  checked: boolean | null;
}

export type Align = 'left' | 'center' | 'right' | null;

/** Phase 15e: which answer card a model's `oppy-kart` block is becoming, when its JSON says so already */
export type PendingCardKind = 'calendar' | 'mail_list' | 'attachment' | null;

export type Block =
  | { kind: 'heading'; level: number; children: Inline[] }
  | { kind: 'paragraph'; children: Inline[][] }
  | { kind: 'list'; ordered: boolean; start: number; items: ListItem[] }
  | { kind: 'code'; language: string | null; text: string }
  | { kind: 'table'; align: Align[]; head: Inline[][]; rows: Inline[][][] }
  | { kind: 'quote'; children: Block[] }
  | { kind: 'rule' }
  /**
   * Phase 15e (D-197): a Microsoft 365 answer card the model is still writing. Only drafts have these: the
   * server takes the blocks out of the final text and sends the cards beside it. Never shown as JSON.
   */
  | { kind: 'card-pending'; card: PendingCardKind };

/** The fence language of the answer cards (packages/shared ANSWER_CARD_FENCE). */
const CARD_FENCE = 'oppy-kart';

const CARD_KINDS: Record<string, Exclude<PendingCardKind, null>> = {
  takvim: 'calendar',
  calendar: 'calendar',
  mail: 'mail_list',
  mail_list: 'mail_list',
  ek: 'attachment',
  attachment: 'attachment',
};

/** The card a block's JSON names so far ("kart": "takvim"), so the placeholder has its shape. */
function pendingKind(body: string): PendingCardKind {
  const named = /"(?:kart|kind)"\s*:\s*"([a-z_]+)"/.exec(body)?.[1];
  return (named && CARD_KINDS[named]) || null;
}

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

/** A link target the browser may open; anything else stays text. */
export function safeHref(href: string): string | null {
  const trimmed = href.trim();
  return SAFE_URL.test(trimmed) ? trimmed : null;
}

const INLINE = new RegExp(
  [
    '(`+)([\\s\\S]*?[^`])\\1(?!`)', // code span
    '\\[([^\\]]+)\\]\\(([^)\\s]+)(?:\\s+"[^"]*")?\\)', // [text](url "title")
    '\\*\\*([\\s\\S]+?)\\*\\*', // **bold**
    '__([\\s\\S]+?)__(?!\\w)', // __bold__
    '~~([\\s\\S]+?)~~', // ~~strike~~
    '(?<![\\w*])\\*(?![\\s*])([\\s\\S]*?[^\\s*])\\*(?![\\w*])', // *italic*
    '(?<![\\w_])_(?![\\s_])([\\s\\S]*?[^\\s_])_(?![\\w_])', // _italic_
    "(https?:\\/\\/[^\\s<>()\\[\\]]*[^\\s<>()\\[\\].,;:!?'\"])", // bare link
  ].join('|'),
  'g',
);

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  const push = (value: string) => {
    if (!value) return;
    const previous = out.at(-1);
    if (previous?.kind === 'text') previous.text += value;
    else out.push({ kind: 'text', text: value });
  };
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    push(text.slice(last, index));
    last = index + match[0].length;
    if (match[2] !== undefined) {
      out.push({ kind: 'code', text: match[2].replace(/^ (.*) $/, '$1') });
    } else if (match[3] !== undefined && match[4] !== undefined) {
      const href = safeHref(match[4]);
      if (href) out.push({ kind: 'link', href, children: parseInline(match[3]) });
      else push(match[3]);
    } else if (match[5] !== undefined || match[6] !== undefined) {
      out.push({ kind: 'strong', children: parseInline((match[5] ?? match[6]) as string) });
    } else if (match[7] !== undefined) {
      out.push({ kind: 'del', children: parseInline(match[7]) });
    } else if (match[8] !== undefined || match[9] !== undefined) {
      out.push({ kind: 'em', children: parseInline((match[8] ?? match[9]) as string) });
    } else if (match[10] !== undefined) {
      out.push({ kind: 'link', href: match[10], children: [{ kind: 'text', text: match[10] }] });
    } else {
      push(match[0]);
    }
  }
  push(text.slice(last));
  return out;
}

const FENCE = /^ {0,3}(`{3,}|~{3,})\s*([\w+#.-]*)/;
const HEADING = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^ {0,3}([-*_])(\s*\1){2,}\s*$/;
const LIST_ITEM = /^(\s*)(?:(\d{1,9})[.)]|[-*+])\s+(.*)$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

function splitRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  const cells: string[] = [];
  let current = '';
  let inCode = false;
  for (let i = 0; i < row.length; i += 1) {
    const ch = row[i] as string;
    if (ch === '\\' && row[i + 1] === '|') {
      current += '|';
      i += 1;
    } else if (ch === '`') {
      inCode = !inCode;
      current += ch;
    } else if (ch === '|' && !inCode) {
      cells.push(current.trim());
      current = '';
    } else current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function isTableStart(lines: string[], i: number): boolean {
  const header = lines[i] ?? '';
  const separator = lines[i + 1] ?? '';
  return header.includes('|') && TABLE_SEPARATOR.test(separator) && separator.includes('-') && splitRow(header).length === splitRow(separator).length;
}

function indentOf(line: string): number {
  const match = /^(\s*)/.exec(line);
  return (match?.[1] ?? '').replace(/\t/g, '    ').length;
}

/** Lists by indentation: an item indented further than its parent's text starts a nested list. */
function parseList(lines: string[], start: number): { block: Block; next: number } {
  const first = LIST_ITEM.exec(lines[start] as string) as RegExpExecArray;
  const baseIndent = indentOf(lines[start] as string);
  const ordered = first[2] !== undefined;
  const block: Extract<Block, { kind: 'list' }> = { kind: 'list', ordered, start: ordered ? Number(first[2]) : 1, items: [] };
  let i = start;
  while (i < lines.length) {
    const line = lines[i] as string;
    const match = LIST_ITEM.exec(line);
    if (match && indentOf(line) === baseIndent && (match[2] !== undefined) === ordered) {
      let text = match[3] as string;
      let checked: boolean | null = null;
      const task = /^\[([ xX])\]\s+(.*)$/.exec(text);
      if (task) {
        checked = task[1] !== ' ';
        text = task[2] as string;
      }
      const item: ListItem = { children: parseInline(text), sub: null, checked };
      block.items.push(item);
      i += 1;
      // continuation lines and nested lists
      while (i < lines.length) {
        const next = lines[i] as string;
        if (!next.trim()) {
          const after = lines[i + 1];
          if (after !== undefined && indentOf(after) > baseIndent && after.trim()) {
            i += 1;
            continue;
          }
          break;
        }
        const nested = LIST_ITEM.exec(next);
        if (nested && indentOf(next) > baseIndent) {
          const parsed = parseList(lines, i);
          item.sub = parsed.block;
          i = parsed.next;
          continue;
        }
        if (!nested && indentOf(next) > baseIndent) {
          item.children.push({ kind: 'text', text: ' ' }, ...parseInline(next.trim()));
          i += 1;
          continue;
        }
        break;
      }
      continue;
    }
    break;
  }
  return { block, next: i };
}

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] as string;
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const marker = fence[1] as string;
      const language = fence[2] ?? '';
      // a fence still being written as the draft's last line may be the start of a card's ("```oppy")
      const cardStarting = i === lines.length - 1 && language.length > 0 && CARD_FENCE.startsWith(language);
      const code: string[] = [];
      for (i += 1; i < lines.length && !(lines[i] as string).trim().startsWith(marker); i += 1) code.push(lines[i] as string);
      i += 1;
      if (language === CARD_FENCE || cardStarting) blocks.push({ kind: 'card-pending', card: pendingKind(code.join('\n')) });
      else blocks.push({ kind: 'code', language: language || null, text: code.join('\n') });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: (heading[1] as string).length, children: parseInline(heading[2] as string) });
      i += 1;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' });
      i += 1;
      continue;
    }
    if (/^ {0,3}>/.test(line)) {
      const quoted: string[] = [];
      for (; i < lines.length && /^ {0,3}>/.test(lines[i] as string); i += 1) quoted.push((lines[i] as string).replace(/^ {0,3}> ?/, ''));
      blocks.push({ kind: 'quote', children: parseMarkdown(quoted.join('\n')) });
      continue;
    }
    if (isTableStart(lines, i)) {
      const head = splitRow(line);
      const align: Align[] = splitRow(lines[i + 1] as string).map((cell) =>
        cell.startsWith(':') && cell.endsWith(':') ? 'center' : cell.endsWith(':') ? 'right' : cell.startsWith(':') ? 'left' : null,
      );
      const rows: Inline[][][] = [];
      for (i += 2; i < lines.length && (lines[i] as string).includes('|') && (lines[i] as string).trim(); i += 1) {
        const cells = splitRow(lines[i] as string);
        rows.push(head.map((_, n) => parseInline(cells[n] ?? '')));
      }
      blocks.push({ kind: 'table', align, head: head.map((cell) => parseInline(cell)), rows });
      continue;
    }
    if (LIST_ITEM.exec(line)) {
      const parsed = parseList(lines, i);
      blocks.push(parsed.block);
      i = parsed.next;
      continue;
    }
    // a paragraph runs until an empty line or another block starts
    const paragraph: Inline[][] = [];
    for (; i < lines.length; i += 1) {
      const current = lines[i] as string;
      if (!current.trim() || FENCE.test(current) || HEADING.test(current) || RULE.test(current) || /^ {0,3}>/.test(current) || isTableStart(lines, i)) break;
      if (paragraph.length > 0 && LIST_ITEM.test(current)) break;
      paragraph.push(parseInline(current.trim()));
    }
    blocks.push({ kind: 'paragraph', children: paragraph });
  }
  return blocks;
}
